import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { isRecruiterChannelEnabled } from '@kilat/shared';

import { useAgentId, useAuth, useRecruiterId } from '../lib/use-auth.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useI18n } from '../lib/use-i18n.js';
import {
  BottomTabBar,
  BottomTabSpacer,
  BuildingIcon,
  HomeIcon,
  MedalIcon,
  ReceiptIcon,
  TagIcon,
  ThemeToggle,
  UserIcon,
  type BottomTab,
} from '@kilat/ui';

import { LocaleSwitcher } from './locale-switcher.js';
import { InboxBell } from './inbox-bell.js';
/**
 * Copy for the appearance control.
 *
 * A helper rather than an inline object literal because this file renders the
 * control in more than one place, and a literal in each is how the same control
 * ends up with two different labels after someone edits one of them.
 */
function appearanceLabels(t: { t: (key: string) => string }) {
  return {
    group: t.t('common.appearance.group'),
    system: t.t('common.appearance.system'),
    light: t.t('common.appearance.light'),
    dark: t.t('common.appearance.dark'),
  };
}

/**
 * Header for the agent-pwa shell. Sprint 85 — the same shell hosts
 * both the agent and the recruiter personas. A signed-in user can
 * carry one or both claims (often both — most recruiters started as
 * agents and stayed that way).
 *
 * Nav rules:
 *   - Hidden entirely until the user is signed in AND carries at least
 *     one of {agent, recruiter}.
 *   - Agent destinations (Home / Earnings / Operators / Code) render
 *     only when `agentId` resolves.
 *   - Recruiter destinations (/recruiter, /recruiter/agents, ...) render
 *     only when `recruiterId` resolves.
 *   - When both are present, the two groups sit side-by-side with a
 *     thin divider so the user can navigate between them; the
 *     active-pill highlight makes the current persona obvious.
 *
 * No-dead-ends: a user who is recruiter-only (no agent claim) lands on
 * `/` and HomePage redirects them into the recruiter dashboard.
 */
export function AppShell({ children }: { readonly children: React.ReactNode }) {
  const { user, signOut } = useAuth();
  const brand = usePlatformBrand();
  const agentId = useAgentId();
  const recruiterId = useRecruiterId();
  // ADR-0023: a recruiter claim only surfaces the recruiter persona when the
  // brand's recruiter channel is enabled. When it's off, a recruiter-only user
  // is treated as having no destinations here (the routes redirect to `/`).
  const isRecruiter = Boolean(recruiterId) && isRecruiterChannelEnabled(brand);
  const showNav = Boolean(user && (agentId || isRecruiter));
  return (
    <>
      {showNav ? (
        <>
          <Header onSignOut={signOut} isAgent={Boolean(agentId)} isRecruiter={isRecruiter} />
          {/* Phone-only. The header's pills already show every destination at
              720px and up; this puts the primary five under the thumb where a
              hamburger used to hide all of them. */}
          <MobileTabs isAgent={Boolean(agentId)} isRecruiter={isRecruiter} />
        </>
      ) : (
        // Slim header for the signed-out / no-claim states — same chrome
        // row, just brand label on the left and the LocaleSwitcher on
        // the right. Avoids the position-fixed overlap with page content
        // we shipped initially.
        <SignedOutHeader />
      )}
      {children}
      {showNav ? <BottomTabSpacer /> : null}
    </>
  );
}

/**
 * The five destinations a phone gets, per persona.
 *
 * An agent's five are Home, Earnings, Operators, Code and Profile: the ones
 * they open between visits. `/payouts` and `/map` stay in the header menu —
 * payouts is a monthly errand and the map is a planning screen, and cutting to
 * five is the constraint that keeps every target thumb-sized at 360px.
 *
 * A recruiter's five mirror their own subtree. A user who is BOTH sees the
 * recruiter set while inside `/recruiter` and the agent set everywhere else,
 * which matches the section they are actually working in rather than making
 * them choose a persona before they can navigate.
 */
function MobileTabs({
  isAgent,
  isRecruiter,
}: {
  readonly isAgent: boolean;
  readonly isRecruiter: boolean;
}) {
  const { pathname } = useLocation();
  const t = useI18n();
  const inRecruiter = pathname.startsWith('/recruiter');
  const showRecruiterSet = isRecruiter && (inRecruiter || !isAgent);

  const tabs: BottomTab[] = showRecruiterSet
    ? [
        {
          to: '/recruiter',
          label: t.t('agent.nav.dashboard'),
          icon: <HomeIcon size={20} />,
          active: pathname === '/recruiter',
        },
        {
          to: '/recruiter/agents',
          label: t.t('agent.recruiterHome.myAgents'),
          icon: <UserIcon size={20} />,
          active: pathname.startsWith('/recruiter/agents'),
        },
        {
          to: '/recruiter/earnings',
          label: t.t('agent.nav.earnings'),
          icon: <ReceiptIcon size={20} />,
          active: pathname.startsWith('/recruiter/earnings'),
        },
        {
          to: '/recruiter/code',
          label: t.t('agent.nav.code'),
          icon: <TagIcon size={20} />,
          active: pathname.startsWith('/recruiter/code'),
        },
        {
          to: '/profile',
          label: t.t('agent.nav.profile'),
          icon: <MedalIcon size={20} />,
          active: pathname.startsWith('/profile'),
        },
      ]
    : [
        {
          to: '/',
          label: t.t('agent.nav.home'),
          icon: <HomeIcon size={20} />,
          active: (pathname === '/' || pathname === '/home') && !inRecruiter,
        },
        {
          to: '/earnings',
          label: t.t('agent.nav.earnings'),
          icon: <ReceiptIcon size={20} />,
          active: pathname.startsWith('/earnings'),
        },
        {
          to: '/operators',
          label: t.t('agent.nav.operators'),
          icon: <BuildingIcon size={20} />,
          active: pathname.startsWith('/operators'),
        },
        {
          to: '/code',
          label: t.t('agent.nav.code'),
          icon: <TagIcon size={20} />,
          active: pathname === '/code' || pathname.startsWith('/code/'),
        },
        {
          to: '/profile',
          label: t.t('agent.nav.profile'),
          icon: <MedalIcon size={20} />,
          active: pathname.startsWith('/profile'),
        },
      ];

  if (!isAgent && !isRecruiter) return null;

  return (
    <BottomTabBar
      tabs={tabs}
      ariaLabel={t.t('agent.nav.primary')}
      renderLink={(tab, children) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          style={{ textDecoration: 'none', color: 'inherit' }}
          aria-current={tab.active ? 'page' : undefined}
        >
          {children}
        </NavLink>
      )}
    />
  );
}

function SignedOutHeader() {
  const t = useI18n();
  const brand = usePlatformBrand();
  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        padding: 'var(--space-3) var(--space-4)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontWeight: 600,
          letterSpacing: '0.01em',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
        aria-label={`${brand.name} Agent`}
      >
        {brand.shortName}
      </span>
      <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
        <ThemeToggle compact labels={appearanceLabels(t)} />
        <LocaleSwitcher />
      </div>
    </header>
  );
}

function Header({
  onSignOut,
  isAgent,
  isRecruiter,
}: {
  readonly onSignOut: () => Promise<void>;
  readonly isAgent: boolean;
  readonly isRecruiter: boolean;
}) {
  const { pathname } = useLocation();
  const brand = usePlatformBrand();
  const t = useI18n();
  const isCompact = useIsCompact();
  const [navOpen, setNavOpen] = useState(false);

  const inRecruiter = pathname.startsWith('/recruiter');
  const isProfile = pathname.startsWith('/profile');
  const isHome = (pathname === '/' || pathname === '/home') && !inRecruiter;
  const isEarnings = pathname.startsWith('/earnings');
  const isPayouts = pathname.startsWith('/payouts');
  const isOperators = pathname.startsWith('/operators');
  const isMap = pathname === '/map' || pathname.startsWith('/map/');
  const isCode = pathname === '/code' || pathname.startsWith('/code/');
  const isRDash = pathname === '/recruiter';
  const isRAgents = pathname.startsWith('/recruiter/agents');
  const isREarnings = pathname.startsWith('/recruiter/earnings');
  const isRPayouts = pathname.startsWith('/recruiter/payouts');
  const isRCode = pathname.startsWith('/recruiter/code');

  const showLabel = isAgent && isRecruiter; // dual-persona disambiguator

  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  const inlineNav = (
    <nav
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        flexWrap: 'wrap',
        minWidth: 0,
      }}
    >
      {isAgent ? (
        <>
          {showLabel ? <SectionLabel>{t.t('agent.nav.agentSection')}</SectionLabel> : null}
          <NavPill to="/" active={isHome} label={t.t('agent.nav.home')} />
          <NavPill to="/earnings" active={isEarnings} label={t.t('agent.nav.earnings')} />
          <NavPill to="/payouts" active={isPayouts} label={t.t('agent.nav.payouts')} />
          <NavPill to="/operators" active={isOperators} label={t.t('agent.nav.operators')} />
          <NavPill to="/map" active={isMap} label={t.t('agent.nav.map')} />
          <NavPill to="/code" active={isCode} label={t.t('agent.nav.code')} />
        </>
      ) : null}
      {isAgent && isRecruiter ? <Divider /> : null}
      {isRecruiter ? (
        <>
          {showLabel ? <SectionLabel>{t.t('agent.nav.recruiterSection')}</SectionLabel> : null}
          <NavPill
            to="/recruiter"
            active={isRDash}
            label={showLabel ? t.t('agent.nav.dashboard') : t.t('agent.nav.recruiterSection')}
          />
          <NavPill
            to="/recruiter/agents"
            active={isRAgents}
            label={t.t('agent.recruiterHome.myAgents')}
          />
          <NavPill
            to="/recruiter/earnings"
            active={isREarnings}
            label={t.t('agent.nav.earnings')}
          />
          <NavPill to="/recruiter/payouts" active={isRPayouts} label={t.t('agent.nav.payouts')} />
          <NavPill to="/recruiter/code" active={isRCode} label={t.t('agent.nav.code')} />
        </>
      ) : null}
      <Divider />
      <NavPill to="/profile" active={isProfile} label={t.t('agent.nav.profile')} />
    </nav>
  );

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <div
        style={{
          padding: 'var(--space-3) var(--space-4)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontWeight: 600,
            letterSpacing: '0.01em',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
          aria-label={`${brand.name} ${
            isAgent ? t.t('agent.nav.agentSection') : t.t('agent.nav.recruiterSection')
          }`}
        >
          {brand.shortName}
        </span>

        {!isCompact ? (
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              flexWrap: 'wrap',
            }}
          >
            {inlineNav}
            <ThemeToggle compact labels={appearanceLabels(t)} />
            <InboxBell />
            <LocaleSwitcher />
            <SignOutButton onClick={onSignOut} />
          </div>
        ) : (
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              flexShrink: 0,
            }}
          >
            {/*
              No appearance control here.

              This row is flexShrink:0, and brand + a three-segment Appearance
              radiogroup + the locale pill + the hamburger measured 312px on a
              360px viewport — 15px of horizontal overflow, which failed six
              e2e tests. Appearance moved into the menu this hamburger already
              opens: it is a setting, not a per-screen action, and stacking a
              third control beside a hamburger is the corner pile-up the
              responsive contract exists to prevent.
              The bell is the one addition this row takes: brand + bell + locale
              pill + hamburger, all touch-target sized — measured at a 360-wide viewport in
              the report for this change.
            */}
            <InboxBell />
            <LocaleSwitcher />
            <button
              type="button"
              onClick={() => setNavOpen((v) => !v)}
              aria-label={t.t('agent.nav.menu')}
              aria-expanded={navOpen}
              style={{
                /* var(--touch-target): clamp(2.75rem, 1.5vw + 2.5rem, 3.25rem) — 44px → 52px,
                   scales with viewport + respects user a11y font-scale. */
                minWidth: 'var(--touch-target)',
                minHeight: 'var(--touch-target)',
                width: 'var(--touch-target)',
                height: 'var(--touch-target)',
                borderRadius: '999px',
                border: '1px solid var(--color-border-strong)',
                background: 'var(--color-surface)',
                color: 'var(--color-on-surface)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
                flexShrink: 0,
              }}
            >
              <HamburgerIcon open={navOpen} />
            </button>
          </div>
        )}
      </div>

      {isCompact && navOpen ? (
        <div
          style={{
            display: 'grid',
            gap: 'var(--space-1)',
            padding: 'var(--space-3) var(--space-4) var(--space-4) var(--space-4)',
            borderTop: '1px solid var(--color-border)',
            background: 'rgba(255,255,255,0.98)',
          }}
        >
          {inlineNav}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-2)',
              padding: 'var(--space-2) 0',
            }}
          >
            <span style={{ fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}>
              {t.t('agent.nav.appearance')}
            </span>
            <ThemeToggle compact labels={appearanceLabels(t)} />
          </div>
          <SignOutButton onClick={onSignOut} fullWidth />
        </div>
      ) : null}
    </header>
  );
}

function SignOutButton({
  onClick,
  fullWidth,
}: {
  readonly onClick: () => Promise<void>;
  readonly fullWidth?: boolean;
}) {
  const t = useI18n();
  return (
    <button
      type="button"
      onClick={() => {
        void onClick();
      }}
      style={{
        padding: '0.5em 0.875em',
        minHeight: 'var(--touch-target)',
        borderRadius: 'var(--radius-pill)',
        border: '1px solid var(--color-border-strong)',
        background: 'transparent',
        color: 'var(--color-on-surface-muted)',
        fontSize: 'var(--text-meta)',
        lineHeight: 1.3,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        ...(fullWidth ? { width: '100%', marginTop: 'var(--space-2)' } : {}),
      }}
    >
      {t.t('agent.nav.signOut')}
    </button>
  );
}

function HamburgerIcon({ open }: { readonly open: boolean }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden focusable="false">
      <path
        d={open ? 'M6 6 L18 18 M18 6 L6 18' : 'M4 7 H20 M4 12 H20 M4 17 H20'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function useIsCompact(): boolean {
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 720px)');
    const onChange = () => setCompact(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return compact;
}

function NavPill({
  to,
  active,
  label,
}: {
  readonly to: string;
  readonly active: boolean;
  readonly label: string;
}) {
  return (
    <NavLink
      to={to}
      style={{
        /* em padding scales with the pill's own font-size when the user
           bumps OS font-scale; lineHeight keeps tall scripts (ta/zh)
           from cropping; whiteSpace stops multi-word labels from
           wrapping the pill into 2 lines. */
        padding: '0.5em 0.875em',
        minHeight: 'var(--touch-target)',
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 'var(--radius-pill)',
        background: active ? 'var(--color-primary)' : 'transparent',
        color: active ? 'var(--color-primary-fg)' : 'var(--color-on-surface)',
        textDecoration: 'none',
        fontWeight: 500,
        fontSize: 'var(--text-meta)',
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </NavLink>
  );
}

function SectionLabel({ children }: { readonly children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: '0.625rem',
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--color-on-surface-muted)',
        padding: '0 var(--space-1)',
      }}
    >
      {children}
    </span>
  );
}

function Divider() {
  return (
    <span
      aria-hidden
      style={{
        width: 1,
        height: 18,
        background: 'var(--color-border-strong)',
        margin: '0 var(--space-2)',
      }}
    />
  );
}
